import type { CreateAsyncOfficeConvertCommandInput } from "@amzn/ads-blink-client";
import { AsyncExecutionStatus } from "@amzn/ads-blink-client";
import type {
    AssetLocation,
    TemplateMetadata,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { TextButton } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";
import React from "react";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../../../api/blinkApi/blinkApi";
import { getCdbApiClient } from "../../../../api/cdbApi/cdbApi";
import { INSIGHT_DETAILS_TRIGGER_TEXT } from "../../../../config/insights.config";
import { TELEMETRY_ID } from "../../../../constants/telemetry";
import {
    InsightFamilyMetadataAttributes,
    InsightFamilyMetadataBrandType,
} from "../../../../hooks/insightFamilies/useInsightFamiliesFiltersAction";
import { useInsightIdentifierEffect } from "../../../../hooks/misc/useInsightIdentifierEffect";
import { useAsyncErrorStatusNotification } from "../../../../hooks/notifications/useAsyncErrorStatusNotification";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { discoveryFilterStore } from "../../../../store/filters";
import { globalWorkbenchStore } from "../../../../store/globalWorkbench";
import {
    notificationStore,
    NotificationType,
} from "../../../../store/notification";
import { reportIssueStore } from "../../../../store/reportIssue";
import { workbenchStore } from "../../../../store/workbench";
import { AquilaThumbsFeedbackPlacement } from "../../../../types/aquila";
import type { CardDetail } from "../../../../types/card";
import type { DownloadAsset } from "../../../../types/common";
import { isLoading } from "../../../../utils/api.utils";
import { getMarketplaceId } from "../../../../utils/permission.utils";
import {
    logViewInsightDetailsClick,
    logWithTakt,
} from "../../../../utils/telemetry.utils";
import { AquilaThumbsFeedback } from "../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { useSetForgeInsightFamilyDetails } from "../../../forgeVisualize/useSetForgeInsightFamilyDetails";
import { StyledActionsContainer } from "../../commonStyles";
import { InsightSelectionActionText } from "../InsightSelectionActionText";

import { useSetInsightFamilyDetails } from "./useSetInsightFamilyDetails";

const FORGE_ENABLED_FAMILIES = [
    "AudienceFunnelSizing",
    "PeerBenchmarking",
    "BrandLoyalty",
    "BrandedSearchTrends",
    "PathToPurchase",
    "ShareOfVoice",
];

export interface InsightFamilyCardActionProps {
    identifier: string;
    familyName: string;
    metadata: Record<InsightFamilyMetadataAttributes, string>;
    insightFamilyTrackingId: string;
    details: CardDetail[];
    title: string;
    selectedInsightSubtypeIds?: string[];
    index?: number;
    brandId?: string;
}

export const InsightFamilyCardAction: React.FunctionComponent<
    InsightFamilyCardActionProps
> = ({
    identifier,
    familyName,
    brandId,
    title,
    details,
    selectedInsightSubtypeIds,
    index,
    insightFamilyTrackingId,
    metadata,
}) => {
    const marketplaceId = getMarketplaceId();
    const [useUnifiedSearch, thumbsFeedbackCard, forgeViz, globalWorkbench] =
        useStore(FeatureFlagsStore, (state) => [
            state.unifiedSearch,
            state.thumbsFeedbackCard,
            state.forgeViz,
            state.globalWorkbench,
        ]);

    const [getSlidesSelected] = useStore(globalWorkbenchStore, (state) => [
        state.getSlidesSelected,
    ]);

    const [contentSubType, filters] = useStore(
        discoveryFilterStore,
        (state) => [state.getContentSubType(), state.getFilters()],
    );
    const insightsData = filters[contentSubType.provider].data;
    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const [updateReportIssueContextData] = useStore(
        reportIssueStore,
        (state) => [state.updateReportIssueContextData],
    );

    const [previewAsset, setPreviewAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);
    const [downloadAsset, setDownloadAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);

    const [templateInfo, setTemplateInfo] = React.useState<
        TemplateMetadata | undefined
    >(undefined);

    // Hardcoded to ensure we are not impacted for latency
    // Will fetch this info from publisher portal and attach it to
    // each card element.
    const isForgeEnabledFamily = FORGE_ENABLED_FAMILIES.includes(familyName);

    const [previewState, previewAction] = useAsync(
        async (download: AssetLocation, template: TemplateMetadata) => {
            const blinkClient = await getBlinkApiClientV2();
            const resourceIdentifier = `${insightFamilyTrackingId}.${template.templateId}.${template.templateVersion}`;
            const outputLocationKey = (download.key ?? "").replace(
                /\.pptx$/,
                ".pdf",
            );
            const input: CreateAsyncOfficeConvertCommandInput = {
                clientId: "IHUB",
                resourceIdentifier,
                templateIdentifer: template.name,
                applicationContext: "InsightFamilyCardAction",
                templateOrigin: "PublisherPortal",
                inputLocation: download,
                outputLocation: {
                    bucket: download.bucket ?? "",
                    key: outputLocationKey,
                },
                format: "pdf",
            };
            const { execution, status } =
                await blinkClient.createAsyncOfficeConvert(input);
            if (status === AsyncExecutionStatus.SUCCESS) {
                logWithTakt(TELEMETRY_ID.ASYNC_OFFICE_CONVERT_CACHE_HIT, {
                    insightFamily: identifier,
                });
            } else {
                logWithTakt(TELEMETRY_ID.ASYNC_OFFICE_CONVERT_CACHE_MISS, {
                    insightFamily: identifier,
                });
            }
            try {
                const convertResponse =
                    await blinkClient.pollGetAsyncOfficeConvert({
                        executionId: execution,
                    });
                setPreviewAsset({
                    content: {
                        bucket: convertResponse.outputLocation?.bucket,
                        key: convertResponse.outputLocation?.key,
                    },
                    downloadUrl: convertResponse.outputFileDownloadUrl,
                });
            } catch (err: any) {
                logWithTakt(TELEMETRY_ID.ASYNC_OFFICE_CONVERT_ERROR, {
                    insightFamily: identifier,
                });
                upsertNotification(
                    "Insight Preview Failed",
                    NotificationType.ERROR,
                );
            }
        },
    );

    const [downloadState, downloadAction] = useAsync(async () => {
        if (forgeViz && isForgeEnabledFamily) {
            getCdbApiClient()
                .then((client) =>
                    client.getInsightFamilyDownload({
                        marketplaceId,
                        familyInstanceId: identifier,
                        selectedInsightSubtypeIds,
                    }),
                )
                .then((data) => setTemplateInfo(data?.template))
                .catch((_) => {
                    throw new Error("Missing template metadata");
                });
            return;
        }

        const data = await (
            await getCdbApiClient()
        ).getInsightFamilyDownload({
            marketplaceId,
            familyInstanceId: identifier,
            selectedInsightSubtypeIds,
        });

        setTemplateInfo(data?.template);
        setDownloadAsset(data as DownloadAsset);

        if (data.content && data.template) {
            await previewAction.execute(data.content, data?.template);
        } else {
            throw new Error("Missing content or template");
        }
    });

    useAsyncErrorStatusNotification(downloadState);
    useAsyncErrorStatusNotification(previewState);

    const handleInsightDetails = async (): Promise<void> => {
        logViewInsightDetailsClick(
            identifier,
            (index ?? 0) + 1,
            insightsData.length,
            undefined,
            useUnifiedSearch,
        );
        await downloadAction.execute();

        updateReportIssueContextData({
            identifier,
            title,
            insightType: InsightType.INSIGHT,
            insightFamilyName: familyName,
        });
        if (isForgeEnabledFamily && forgeViz) {
            setForgeInsightFamilyDetails();
        } else {
            setInsightFamilyDetails();
        }
    };

    const setInsightFamilyDetails = useSetInsightFamilyDetails({
        identifier,
        familyName,
        insightFamilyTrackingId,
        insightType: InsightType.INSIGHT,
        details,
        title,
        previewAsset,
        downloadAsset,
        templateInfo,
        previewLoading: isLoading(previewState.status),
        downloadLoading: isLoading(downloadState.status),
        icmVersion: metadata[InsightFamilyMetadataAttributes.ICM_BRAND_VERSION],
        businesslineId:
            metadata[InsightFamilyMetadataAttributes.BRAND_TYPE] ===
            (InsightFamilyMetadataBrandType.ICM_BRAND as string)
                ? brandId
                : undefined,
        reset: () => {
            setPreviewAsset(undefined);
            setDownloadAsset(undefined);
        },
    });

    const setForgeInsightFamilyDetails = useSetForgeInsightFamilyDetails({
        identifier,
        familyName,
        insightFamilyTrackingId,
        insightType: InsightType.INSIGHT,
        details,
        title,
        templateInfo,
        previewLoading: false,
        downloadLoading: isLoading(downloadState.status),
        icmVersion: metadata[InsightFamilyMetadataAttributes.ICM_BRAND_VERSION],
        businesslineId:
            metadata[InsightFamilyMetadataAttributes.BRAND_TYPE] ===
            (InsightFamilyMetadataBrandType.ICM_BRAND as string)
                ? brandId
                : undefined,
        reset: () => {
            setDownloadAsset(undefined);
        },
    });

    useInsightIdentifierEffect(identifier, handleInsightDetails);
    const numSelectedSlides = globalWorkbench
        ? getSlidesSelected(insightFamilyTrackingId).length
        : getSlideIndexes(identifier).size;

    return (
        <StyledActionsContainer>
            <TextButton
                disabled={isEmpty(identifier)}
                onClick={() => void handleInsightDetails()}
                loading={
                    isLoading(previewState.status) ||
                    isLoading(downloadState.status)
                }
            >
                {INSIGHT_DETAILS_TRIGGER_TEXT}
            </TextButton>
            {numSelectedSlides > 0 && (
                <InsightSelectionActionText
                    identifier={identifier}
                    numSelectedSlides={numSelectedSlides}
                />
            )}
            {numSelectedSlides === 0 && thumbsFeedbackCard && (
                <AquilaThumbsFeedback
                    identifier={identifier}
                    insightType={InsightType.INSIGHT}
                    placement={AquilaThumbsFeedbackPlacement.CARD}
                />
            )}
        </StyledActionsContainer>
    );
};
